import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Download, Copy, Megaphone, ImageIcon } from "lucide-react";
import { WhatsAppIcon } from "@/components/social";
import { Twitter, Facebook, Link2 } from "lucide-react";

const money = (n) => `€${(Number(n) || 0).toFixed(2)}`;

const downloadImage = async (url, filename) => {
  try {
    const res = await fetch(url, { mode: "cors" });
    const blob = await res.blob();
    const href = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = href; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(href);
    toast.success("Banner downloaded");
  } catch {
    window.open(url, "_blank", "noopener,noreferrer");
  }
};

const ShareRow = ({ shareUrl, caption, testPrefix }) => {
  const enc = encodeURIComponent(shareUrl);
  const encCap = encodeURIComponent(caption);
  const open = (href) => window.open(href, "_blank", "noopener,noreferrer,width=600,height=520");
  const copy = async () => {
    try { await navigator.clipboard.writeText(`${caption} ${shareUrl}`); toast.success("Share text copied"); }
    catch { toast.error("Could not copy"); }
  };
  const Btn = ({ onClick, label, testid, children }) => (
    <button type="button" aria-label={label} data-testid={testid} onClick={onClick}
      className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-muted-foreground transition-colors hover:bg-primary hover:text-primary-foreground">
      {children}
    </button>
  );
  return (
    <div className="flex items-center gap-1.5">
      <Btn onClick={() => open(`https://wa.me/?text=${encCap}%20${enc}`)} label="Share on WhatsApp" testid={`${testPrefix}-whatsapp`}><WhatsAppIcon className="w-4 h-4" /></Btn>
      <Btn onClick={() => open(`https://www.facebook.com/sharer/sharer.php?u=${enc}`)} label="Share on Facebook" testid={`${testPrefix}-facebook`}><Facebook className="w-4 h-4" /></Btn>
      <Btn onClick={() => open(`https://twitter.com/intent/tweet?url=${enc}&text=${encCap}`)} label="Share on X" testid={`${testPrefix}-twitter`}><Twitter className="w-4 h-4" /></Btn>
      <Btn onClick={copy} label="Copy share text" testid={`${testPrefix}-copy`}><Link2 className="w-4 h-4" /></Btn>
    </div>
  );
};

export const PartnerMarketingKit = () => {
  const [data, setData] = useState(null);
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    api.get("/partners/marketing-kit").then((r) => setData(r.data)).catch(() => setData({ error: true }));
  }, []);

  if (!data) return <Card className="p-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></Card>;
  if (data.error) return null;

  const ref = data.ref_code;
  const bannerCaption = `Shop authentic African products on Xurray Mart — delivered across Europe! 🌍🛒 ${origin}/?ref=${ref}`;

  return (
    <div className="space-y-5" data-testid="marketing-kit">
      {/* Banners */}
      <Card className="p-5">
        <div className="flex items-center gap-2 mb-1">
          <span className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><ImageIcon className="w-4 h-4" /></span>
          <h3 className="font-semibold">Ready-made banners</h3>
        </div>
        <p className="text-xs text-muted-foreground mb-4">Download a banner, post it, and paste your caption (with your link built in) below.</p>
        <div className="grid sm:grid-cols-3 gap-4">
          {(data.banners || []).map((b) => (
            <div key={b.id} className="rounded-xl border overflow-hidden bg-muted/30" data-testid={`banner-${b.id}`}>
              <div className="aspect-video bg-slate-900 flex items-center justify-center overflow-hidden">
                <img src={b.image_url} alt={b.label} className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="p-3">
                <p className="text-xs font-medium truncate">{b.label}</p>
                <p className="text-[10px] text-muted-foreground mb-2">{b.ratio}</p>
                <Button size="sm" variant="outline" className="w-full rounded-full text-xs" data-testid={`banner-download-${b.id}`}
                  onClick={() => downloadImage(b.image_url, `xurray-banner-${b.id}.jpg`)}>
                  <Download className="w-3.5 h-3.5 mr-1" /> Download
                </Button>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 rounded-xl bg-muted/40 p-3 flex items-start gap-2">
          <p className="text-xs text-muted-foreground flex-1 break-words" data-testid="banner-caption">{bannerCaption}</p>
          <Button size="sm" variant="ghost" className="shrink-0" data-testid="copy-banner-caption"
            onClick={() => { navigator.clipboard.writeText(bannerCaption); toast.success("Caption copied"); }}>
            <Copy className="w-4 h-4" />
          </Button>
        </div>
      </Card>

      {/* Product share cards */}
      <Card className="p-5">
        <div className="flex items-center gap-2 mb-1">
          <span className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><Megaphone className="w-4 h-4" /></span>
          <h3 className="font-semibold">Promote products in one tap</h3>
        </div>
        <p className="text-xs text-muted-foreground mb-4">Every link already carries your referral code — you earn when someone buys or signs up.</p>
        {(!data.products || data.products.length === 0) ? (
          <p className="text-sm text-muted-foreground py-4 text-center" data-testid="kit-no-products">No products to promote yet — check back soon.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.products.map((pr) => {
              const shareUrl = `${API}/api/share/product/${pr.product_id}?ref=${ref}`;
              const caption = `Check out "${pr.title}" on Xurray Mart 🛒`;
              return (
                <div key={pr.product_id} className="rounded-xl border overflow-hidden flex flex-col" data-testid={`kit-product-${pr.product_id}`}>
                  <div className="aspect-square bg-muted flex items-center justify-center overflow-hidden">
                    {pr.image ? <img src={pr.image} alt={pr.title} className="w-full h-full object-cover" loading="lazy" />
                      : <ImageIcon className="w-8 h-8 text-muted-foreground" />}
                  </div>
                  <div className="p-3 flex flex-col flex-1">
                    <p className="text-sm font-medium line-clamp-2 leading-snug">{pr.title}</p>
                    <p className="text-xs text-muted-foreground mb-2">{pr.seller_name} · {money(pr.price)}</p>
                    <div className="mt-auto pt-2 border-t flex items-center justify-between">
                      <ShareRow shareUrl={shareUrl} caption={caption} testPrefix={`kit-share-${pr.product_id}`} />
                      <button type="button" data-testid={`kit-copylink-${pr.product_id}`} aria-label="Copy product link"
                        className="text-xs text-primary font-medium hover:underline"
                        onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success("Product link copied"); }}>
                        Copy link
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
};
